import { changeScreen } from '@Redux/actions/user-flow';
import { AppDispatch } from '../../../../store.type';
import { GenericRecordType } from '../../../bene-info-correction/interface';
import { screenNames } from '../../constants';
import { USER_FLOW_JUMP_TO } from '@Redux/action-contants';
import {
  arrowTagPattern,
  CARD_SUPPORTED_ROUTING_CODE,
  CASH_PAYOUT_SUPPORTED_ROUTING_CODE,
  INDIVIDUAL,
  MULTIPLE_TRANSACTION_URL,
  PAYEE_LIST_URL
} from '@Utils/constants';
import { resetNewBeneFlowStateAction } from '@Redux/actions/new-bene-flow';
import { destroy } from 'redux-form';
import {
  BENE_BANK_DETAILS_FORM_NAME,
  BENE_PERSONAL_DETAILS_FORM_NAME,
  LABELS
} from '../../constants/commons';
import {
  storeConfigBeneValidation,
  storeSchemBeneValiation
} from '@Redux/actions/transaction/transaction-action';
import { navigateHistory } from '@Redux/actions/new-bene-flow/new-bene-flow-action';
import { NavigateFunction } from 'react-router';

type ScreenNamesType = keyof typeof screenNames;

export const maskAccountNumber = (accountNumber?: string | number, visibleDigits: number = 4) => {
  if (!accountNumber) return '';

  const accStr = accountNumber.toString();
  return `*${accStr.slice(-visibleDigits)}`;
};

export const getPageHeading = (
  currentScreen: string,
  details_selected: string | undefined,
  isEditFlow: boolean
) => {
  if (currentScreen === screenNames.bank_details.name) {
    const headingMap: Record<string, string> = {
      MONEY_CASH: 'Cash pickup',
      MONEYGRAM_CASH_PAYOUT: 'Cash pickup',
      CASH_PAYOUT: 'Cash pickup',
      CASH: 'Cash pickup',
      WALLET: 'Recipient wallet details',
      CARD: 'Recipient card details',
      PROXY: 'Recipient receiving details'
    };
    const normalizedKey = (details_selected || '').toUpperCase();
    return headingMap[normalizedKey || ''] || screenNames[currentScreen as ScreenNamesType]?.header;
  }
  if (currentScreen === screenNames.recipient_added.name && isEditFlow) return 'Recipient updated';

  return screenNames[currentScreen as ScreenNamesType]?.header;
};

export const handleOnBackClick = (
  dispatch: AppDispatch,
  isRedirectedFromSendMoney: boolean,
  screens: GenericRecordType,
  timeline: GenericRecordType,
  currentScreen: string,
  navigate: NavigateFunction,
  accountType: string,
  hasRecipients: boolean = true
) => {
  const exitTo = () => {
    if (isRedirectedFromSendMoney && hasRecipients) {
      if (accountType === INDIVIDUAL) {
        dispatch(changeScreen(USER_FLOW_JUMP_TO, 'send_money', screens, timeline));
      } else {
        navigate(MULTIPLE_TRANSACTION_URL);
      }
    } else {
      navigate(PAYEE_LIST_URL);
    }
  };

  if (currentScreen === screenNames.receiving_method.name) {
    dispatch(resetNewBeneFlowStateAction());
    exitTo();

    dispatch(destroy(BENE_PERSONAL_DETAILS_FORM_NAME));
    dispatch(destroy(BENE_BANK_DETAILS_FORM_NAME));
    return;
  }
  if (currentScreen === screenNames.bank_details.name) {
    dispatch(storeConfigBeneValidation(null));
    dispatch(storeSchemBeneValiation(null));
  }
  dispatch(navigateHistory({ type: 'POP_NAVIGATION_HISTORY' }));
};

export const handleOnExitClick = (
  dispatch: AppDispatch,
  currentScreen: string,
  isRedirectedFromSendMoney: boolean,
  screens: GenericRecordType,
  timeline: GenericRecordType,
  navigate: NavigateFunction,
  onClose: ((param: boolean) => void) | null,
  accountType: string,
  hasRecipients: boolean = true
) => {
  dispatch(destroy(BENE_PERSONAL_DETAILS_FORM_NAME));
  dispatch(destroy(BENE_BANK_DETAILS_FORM_NAME));
  screenNames[currentScreen as ScreenNamesType]?.formNames.forEach((formName) => {
    dispatch(destroy(formName));
  });
  onClose?.(true);
  if (isRedirectedFromSendMoney && hasRecipients) {
    if (accountType === INDIVIDUAL) {
      dispatch(changeScreen(USER_FLOW_JUMP_TO, 'send_money', screens, timeline));
    } else {
      navigate(MULTIPLE_TRANSACTION_URL);
    }
  } else {
    navigate(PAYEE_LIST_URL);
  }
};

export const processBeneficiaryData = (
  beneficiaryData: any,
  setBeneState: any,
  storeValidationConfig: any
) => {
  /*
  setBeneState will be null when processBeneficiaryData called from BankDetals page
  storeValidationConfig will be null when processBeneficiaryData called from AddEditBeneficiary layout
  */
  let beneBankDetails: GenericRecordType | null = null;
  let beneDebitCardDetails: GenericRecordType | null = null;
  let beneCashPayoutDetails: GenericRecordType | null = null;
  let beneWalletDetails: GenericRecordType | null = null;
  beneBankDetails = beneficiaryData.filter(
    (item: GenericRecordType) => item.routing_config_type === 'BANK'
  );
  beneDebitCardDetails = beneficiaryData.filter((item: GenericRecordType) =>
    CARD_SUPPORTED_ROUTING_CODE.includes(item.routing_config_type)
  );
  beneCashPayoutDetails = beneficiaryData.filter((item: GenericRecordType) =>
    CASH_PAYOUT_SUPPORTED_ROUTING_CODE.includes(item.routing_config_type)
  );
  beneWalletDetails = beneficiaryData.filter(
    (item: GenericRecordType) => item.routing_config_type === 'WALLET'
  );
  if (beneDebitCardDetails) beneDebitCardDetails = beneDebitCardDetails[0];
  if (beneBankDetails) beneBankDetails = beneBankDetails[0];
  if (beneCashPayoutDetails) beneCashPayoutDetails = beneCashPayoutDetails[0];
  if (beneWalletDetails) beneWalletDetails = beneWalletDetails[0];
  let BANK_DETAIL: GenericRecordType | null,
    ADDITIONAL_DETAIL: GenericRecordType | null,
    config_id: GenericRecordType | null;
  if (beneBankDetails) {
    const {
      BANK_DETAIL: bankDetails,
      ADDITIONAL_DETAIL: additionalDetails,
      beneficiary_routing_config_id
    } = beneBankDetails;
    config_id = beneficiary_routing_config_id;
    BANK_DETAIL = bankDetails;
    ADDITIONAL_DETAIL = additionalDetails;
    storeValidationConfig?.({ ...BANK_DETAIL, ...ADDITIONAL_DETAIL });
    setBeneState?.((prevState: any) => ({
      ...prevState,
      bank_validations: { ...BANK_DETAIL, ...ADDITIONAL_DETAIL }
    }));
  }
  let debit_config: GenericRecordType | null = null;
  let debitAdditionalDetails: GenericRecordType | null = null;
  if (beneDebitCardDetails) {
    const {
      BANK_DETAIL: DEBIT_DETAILS,
      ADDITIONAL_DETAIL: DEBIT_ADDITIONAL_DETAILS,
      beneficiary_routing_config_id: debitconfig_id
    } = beneDebitCardDetails;
    debit_config = debitconfig_id;
    storeValidationConfig?.({ ...DEBIT_DETAILS, ...DEBIT_ADDITIONAL_DETAILS });
    setBeneState?.((prevState: any) => ({
      ...prevState,
      debit_validations: { ...DEBIT_DETAILS, ...DEBIT_ADDITIONAL_DETAILS }
    }));
    debitAdditionalDetails = DEBIT_ADDITIONAL_DETAILS;
  }

  let cash_payout_config: GenericRecordType | null = null;
  if (beneCashPayoutDetails) {
    cash_payout_config = beneCashPayoutDetails.beneficiary_routing_config_id;
  }

  let wallet_config: GenericRecordType | null = null;
  let walletAdditionalDetails: GenericRecordType | null = null;

  if (beneWalletDetails) {
    const {
      BANK_DETAIL: WALLET_DETAILS,
      ADDITIONAL_DETAIL: WALLET_ADDITIONAL_DETAIL,
      beneficiary_routing_config_id
    } = beneWalletDetails;
    storeValidationConfig?.({ ...WALLET_DETAILS, ...WALLET_ADDITIONAL_DETAIL });
    setBeneState?.((prevState: any) => ({
      ...prevState,
      wallet_validations: { ...WALLET_DETAILS, ...WALLET_ADDITIONAL_DETAIL }
    }));

    walletAdditionalDetails = WALLET_ADDITIONAL_DETAIL;
    wallet_config = beneficiary_routing_config_id;
  }

  setBeneState?.((prevState: any) => ({
    ...prevState,
    current_bank_fields: BANK_DETAIL,
    additionalFields: ADDITIONAL_DETAIL || debitAdditionalDetails || walletAdditionalDetails,
    config_id,
    debit_config_id: debit_config,
    debit_card_details: beneDebitCardDetails,
    beneCashPayoutDetails: beneCashPayoutDetails,
    cash_payout_config,
    wallet_config,
    beneWalletDetails
  }));
};
export const getRecipientWalletOrAccNumber = (formValues: GenericRecordType) => {
  // Check if the payoutInfo contains a bank account number
  if (formValues?.beneficiary_account_number || formValues?.bank_account_number) {
    return maskAccountNumber(
      (formValues?.beneficiary_account_number || formValues?.bank_account_number) ?? '',
      5
    );
  }
  // If no bank account number, check if a wallet number exists
  if (formValues?.beneficiary_contact_number) {
    return maskAccountNumber(formValues?.beneficiary_contact_number ?? '', 5);
  }
  return '';
};

export const getPayoutVars = (formValues: GenericRecordType, payoutLabel?: string) => {
  const routingConfigType =
    formValues.routing_config_type_temp ||
    formValues.routing_config_type ||
    formValues.payout_method;
  const {
    beneficiary_bank_name,
    bank_name,
    beneficiary_account_number,
    bank_account_number,
    card_number,
    routing_code_value_1,
    proxy_value
  } = formValues;
  const bankName = beneficiary_bank_name || bank_name;
  const beneAccNumber = beneficiary_account_number || bank_account_number;
  const walletNumber = getRecipientWalletOrAccNumber(formValues);
  let paymentLabel, accountNumber, payoutLabelforNonBeneSchema;
  switch (routingConfigType.toUpperCase()) {
    case 'LOCAL':
    case 'BANK':
    case 'IFSC':
    case 'BRI_BANK':
      paymentLabel = bankName;
      accountNumber = maskAccountNumber(beneAccNumber, 4);
      payoutLabelforNonBeneSchema = LABELS.bankDeposit;
      break;
    case 'GEO_SWIFT_CARD':
      paymentLabel = formValues?.card_issuer_name;
      accountNumber = maskAccountNumber(
        card_number?.length === 4 ? 'XXX' + card_number : card_number,
        7
      );
      payoutLabelforNonBeneSchema = LABELS.geoSwift;
      break;
    case 'VISA':
    case 'CARD':
    case 'VISA_DEBIT':
    case 'VISA_DEBIT_CARD':
      paymentLabel = formValues?.card_issuer_name;
      accountNumber = maskAccountNumber(
        card_number?.length === 4 ? 'XXX' + card_number : card_number,
        7
      );
      payoutLabelforNonBeneSchema = LABELS.debitCard;
      break;
    case 'CASH_PAYOUT':
    case 'CASHPAYOUT':
      paymentLabel = formValues?.card_issuer_name || LABELS.availble_partner;
      accountNumber = '';
      payoutLabelforNonBeneSchema = LABELS.cashPickup;
      break;
    case 'MONEYGRAM_CASH_PAYOUT':
      paymentLabel = formValues?.card_issuer_name || LABELS.money_gram;
      accountNumber = '';
      payoutLabelforNonBeneSchema = LABELS.cashPickup;
      break;
    case 'WALLET':
      if (payoutLabel?.toUpperCase() === 'E-WALLET') {
        paymentLabel = `${routing_code_value_1} ${walletNumber}`;
      } else {
        paymentLabel = walletNumber;
      }
      payoutLabelforNonBeneSchema = LABELS.wallet;
      accountNumber = '';
      break;
    case 'PROXY':
      paymentLabel = proxy_value || '';
      accountNumber = '';
      break;
    default:
      accountNumber = '';
      paymentLabel = '';
  }
  paymentLabel = paymentLabel?.replace(arrowTagPattern, '').trim();
  return { paymentLabel, accountNumber, payoutLabelforNonBeneSchema };
};
